import Modal from "@Components/Modal";
import { AppState } from "@Store/index";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import LoginDispatcher from "./actions";
import { Suspense, lazy, useEffect, useState } from "react";
import {
  LoginBodyOverride,
  getLoginBodyOverride,
  subscribeLoginBodyOverride,
} from "./bodyOverride";

import FullPageLoader from "@Components/FullPageLoader";
import useIsDesktop from "hooks/useIsDesktop";
import loadGoogleOneTap from "./helpers/loadGoogleOneTap";
import ModalTitle from "./components/ModalTitle/ModalTitle";
import useTranslationHook from "hooks/useTranslationHook";
import config from "@Config/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useWhitelabel from "hooks/useWhitelabel";
import { updateLoginOpenTapStatus } from "@App/appReducer";
import { removeShortlistParamFromURL } from "@Pages/Search/components/SearchListItem/components/ShortListButton/helper";
import useGlobalSiteLoginBody from "@Components/SiteHeader/useGlobalSiteLoginBody";

import "@Components/SiteHeader/siteLoginModalSkin.scss";

const Login = lazy(() => import("./Login"));

const LoginWrapper = () => {
  // A page's own login body, if one is registered (see ./bodyOverride).
  const [override, setOverride] = useState<LoginBodyOverride>(getLoginBodyOverride());
  useEffect(() => {
    const notify = () => setOverride(getLoginBodyOverride());
    notify();
    return subscribeLoginBodyOverride(notify);
  }, []);
  const {
    showLoginModal,
    loginModalTitle,
    onBack,
    isUserLoggedIn,
    nonce,
    openingSource,
    isVideoModalOpen,
    isReviewMediaGalleryOpen,
  } = useSelector(
    ({ loginData, appData, propertyPageData }: AppState) => ({
      onBack: loginData.onBack,
      loginModalTitle: loginData.modalTitle,
      showLoginModal: loginData.showLoginModal,
      isUserLoggedIn: appData.isUserLoggedIn,
      nonce: appData.nonce,
      openingSource: loginData.openingSource,
      isVideoModalOpen: propertyPageData?.video_modal?.show || false,
      isReviewMediaGalleryOpen: propertyPageData?.review_media_gallery_open || false,
    }),
    shallowEqual,
  );
  const { isWhitelabel } = useWhitelabel();
  useGlobalSiteLoginBody(!isWhitelabel);
  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const loginDispatcher = new LoginDispatcher(dispatch);

  useEffect(() => {
    let timer: any;
    if ((!isUserLoggedIn && !isWhitelabel) || showLoginModal) {
      const loadGoogle = () =>
        loadGoogleOneTap(
          "",
          () => loginDispatcher.onGoogleOneTapLoaded(),
          (status) => dispatch(updateLoginOpenTapStatus(status)),
          nonce,
        );

      if (showLoginModal) {
        // When the modal is already open, load immediately — no reason to
        // make the user stare at a blank Google button for 3.5 s.
        loadGoogle();
      } else {
        // Background One Tap: keep the delay so it doesn't compete with
        // initial page load resources.
        timer = setTimeout(loadGoogle, 3500);
      }
    }
    return () => clearTimeout(timer);
  }, [showLoginModal, isUserLoggedIn]);

  const modalParams: any = isDesktop
    ? {
        size: "loginModalWidth",
        align: "center",
        ...override?.desktopModalParams,
      }
    : {
        animation: "bottomToTop",
        ...override?.mobileModalParams,
      };

  let loaderStyles = { width: "100vw", height: "100vh" };
  if (isDesktop) {
    loaderStyles = { width: "100%", height: "400px" };
  } else if (override) {
    loaderStyles = { width: "100%", height: "min(480px, calc(100vh - 48px))" };
  }
  const { t } = useTranslationHook();
  const translatedLoginModalTitle = t(loginModalTitle);

  return (
    <>
      {showLoginModal && (
        <Modal
          title={
            <ModalTitle
              onBack={onBack || (() => null)}
              shouldBack={Boolean(onBack)}
              loginModalTitle={
                (config.IS_CHINA_APP ? translatedLoginModalTitle : loginModalTitle) ||
                override?.title ||
                ""
              }
            />
          }
          hideModal={() => {
            if (openingSource === "shortlist") {
              removeShortlistParamFromURL();
            }
            loginDispatcher.hideLoginModal();
          }}
          isHistoryPush={!isDesktop && !isVideoModalOpen && !isReviewMediaGalleryOpen}
          hideCloseBtn={Boolean(onBack)}
          {...modalParams}
          zIndex={100001}
          type="login"
        >
          <Suspense fallback={<FullPageLoader {...loaderStyles} />}>
            {override ? <override.Body /> : <Login />}
          </Suspense>
        </Modal>
      )}
    </>
  );
};

export default wrapperHOC(LoginWrapper, {
  componentName: "LoginWrapper-Global",
  showForChina: true,
});
